import type {
  CourseDetails,
  FileLesson,
  Lesson,
  QuizQuestion,
  AssignmentLessonContentData,
} from '../hooks/useCourseApi';
import { API_ORIGIN } from '../apiConfig';

const fileOpenedStorageKey = (courseId: string) => `course_${courseId}_files_opened`;

export function getFileOpenedLessonIds(courseId: string): number[] {
  try {
    const raw = localStorage.getItem(fileOpenedStorageKey(courseId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : [];
  } catch {
    return [];
  }
}

export function markFileLessonOpened(courseId: string, lessonId: number): void {
  const ids = getFileOpenedLessonIds(courseId);
  if (ids.includes(lessonId)) return;
  localStorage.setItem(fileOpenedStorageKey(courseId), JSON.stringify([...ids, lessonId]));
  localStorage.setItem('course_data_updated', String(Date.now()));
}

export function applyFileOpenedProgress(
  courseDetails: CourseDetails,
  courseId: string
): CourseDetails {
  const openedIds = new Set(getFileOpenedLessonIds(courseId));
  if (!openedIds.size) return courseDetails;

  return {
    ...courseDetails,
    units: courseDetails.units.map((unit) => ({
      ...unit,
      lessons: unit.lessons.map((lesson) =>
        lesson.type === 'File' && openedIds.has(lesson.id)
          ? { ...lesson, isFileOpened: true }
          : lesson
      ),
    })),
  };
}

export function getQuizScore(details: unknown): number | null {
  if (!details || typeof details !== 'object') return null;
  const raw = details as Record<string, unknown>;
  const score = raw.score ?? raw.Score;
  if (score === null || score === undefined) return null;
  const n = Number(score);
  return Number.isFinite(n) ? n : null;
}

export function getCourseBookUrl(courseDetails: CourseDetails): string | null {
  const raw = courseDetails as CourseDetails & { courseBook?: string | null };
  const url = courseDetails.coursebook ?? raw.courseBook ?? null;
  return url && String(url).trim() ? String(url).trim() : null;
}

export function normalizeCourseTree(
  courseDetails: CourseDetails,
  courseId: string
): CourseDetails {
  const withTypes: CourseDetails = {
    ...courseDetails,
    coursebook: getCourseBookUrl(courseDetails) ?? undefined,
    units: (courseDetails.units ?? []).map((unit) => ({
      ...unit,
      lessons: (unit.lessons ?? []).map((lesson) => ({
        ...lesson,
        type: normalizeLessonType(lesson.type),
      })) as Lesson[],
    })),
  };
  return applyFileOpenedProgress(withTypes, courseId);
}

export type FileLessonFile = {
  fileName: string;
  fileUrl: string;
  order: number;
};

export type FileLessonDetails = {
  id: number;
  files: FileLessonFile[];
  isCompleted?: boolean;
};

export function normalizeLessonType(type: unknown): 'Video' | 'Quiz' | 'File' | 'Assignment' {
  if (type === 'File' || type === 3 || type === '3') return 'File';
  if (type === 'Assignment' || type === 'assignment' || type === 4 || type === '4') return 'Assignment';
  if (type === 'Quiz' || type === 1 || type === '1') return 'Quiz';
  return 'Video';
}

export function isFileLesson(lesson: Lesson): lesson is FileLesson {
  return lesson.type === 'File';
}

export function isLessonComplete(lesson: Lesson): boolean {
  if (lesson.type === 'Video') return Boolean(lesson.isCompleted);
  if (lesson.type === 'Quiz' || lesson.type === 'Assignment') return Boolean(lesson.isQuizSubmitted);
  if (lesson.type === 'File') return Boolean(lesson.isFileOpened);
  return false;
}

/** GET /api/Student/contentlesson/{id} when the lesson is an assignment (open questions, due date, etc.) */
export function isAssignmentLessonContent(details: unknown): details is AssignmentLessonContentData {
  if (!details || typeof details !== 'object') return false;
  const d = details as Record<string, unknown>;
  return (
    typeof d.lessonId === 'number' &&
    typeof d.hasSubmitted === 'boolean' &&
    Array.isArray(d.questions) &&
    (typeof d.totalGrade === 'number' || typeof d.totalGrade === 'string')
  );
}

/** Quiz payload: MCQ / true-false with `answers` on each question — not an assignment. */
export function isQuizLessonContent(details: unknown): details is { questions: QuizQuestion[] } {
  if (!details || typeof details !== 'object') return false;
  if (isAssignmentLessonContent(details)) return false;
  const questions = (details as { questions?: unknown }).questions;
  if (!Array.isArray(questions) || questions.length === 0) return false;
  const first = questions[0] as Record<string, unknown> | undefined;
  return typeof first === 'object' && first !== null && 'answers' in first;
}

export function isFileLessonResponse(details: unknown): details is FileLessonDetails {
  return (
    !!details &&
    typeof details === 'object' &&
    'files' in details &&
    Array.isArray((details as FileLessonDetails).files)
  );
}

export function resolveFileUrl(url: string): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_ORIGIN}${path}`;
}

export function isPdfFile(file: FileLessonFile): boolean {
  const url = file.fileUrl.toLowerCase();
  const name = file.fileName.toLowerCase();
  return (
    url.includes('.pdf') ||
    name.endsWith('.pdf') ||
    (url.includes('drive.google.com') && url.includes('/file/d/'))
  );
}

export function extractGoogleDriveFileId(url: string): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const fileMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return fileMatch[1];
  const idMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return idMatch[1];
  return null;
}

export function isGoogleDriveUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('drive.google.com') || lower.includes('docs.google.com');
}

export function toGoogleDriveEmbedUrl(url: string): string {
  const fileId = extractGoogleDriveFileId(url);
  if (fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }
  return url;
}

export function toEmbedPdfUrl(url: string): string {
  if (isGoogleDriveUrl(url) && url.includes('/file/d/')) {
    return toGoogleDriveEmbedUrl(url);
  }
  return resolveFileUrl(url);
}

export function isBunnyVideoUrl(url: string): boolean {
  return Boolean(url && url.includes('iframe.mediadelivery.net'));
}

export type VideoPlayerKind = 'bunny' | 'google-drive' | 'react-player';

export function getVideoPlayerKind(url: string): VideoPlayerKind {
  if (!url) return 'react-player';
  if (isBunnyVideoUrl(url)) return 'bunny';
  if (isGoogleDriveUrl(url)) return 'google-drive';
  return 'react-player';
}

export function resolveVideoPlayUrl(url: string): string {
  if (isGoogleDriveUrl(url)) return toGoogleDriveEmbedUrl(url);
  return url;
}
